import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, PenLine, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { StatusChip } from "@/components/app/StatusChip";
import { Skel } from "@/components/app/Common";
import { api, get, errMsg } from "@/lib/api";
import { SJT_TYPES } from "@/lib/assessments";

const LABEL = { none: "Not drafted", generating: "Drafting…", draft: "Draft — needs approval", approved: "Approved", skipped: "Skipped", failed: "Failed" };
const CHIP = { none: "Queued", generating: "Processing", draft: "Needs review", approved: "Completed", skipped: "Skipped", failed: "Failed" };

function ScenarioEditor({ s, i, onChange, onRemove }) {
  const set = (patch) => onChange({ ...s, ...patch });
  const setOpt = (j, patch) => set({ options: s.options.map((o, k) => (k === j ? { ...o, ...patch } : o)) });
  return (
    <div className="space-y-3 rounded-[6px] border border-line p-4" data-testid={`sjt-scenario-${i}`}>
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-ink-muted">#{i + 1}</span>
        <select value={s.type} onChange={(e) => set({ type: e.target.value })} className="h-8 rounded-lg border border-line bg-white px-2 text-xs capitalize" data-testid={`sjt-type-${i}`}>
          {SJT_TYPES.map((t) => <option key={t} value={t} label={t} />)}
        </select>
        <Button size="sm" variant="ghost" className="ml-auto h-7 text-ink-muted" onClick={onRemove} data-testid={`sjt-remove-${i}`}><Trash2 size={14} /></Button>
      </div>
      <Textarea value={s.scenario} onChange={(e) => set({ scenario: e.target.value })} rows={2} data-testid={`sjt-text-${i}`} />
      {s.options.map((o, j) => (
        <div key={o.id || j} className="flex items-center gap-2">
          <Input value={o.text} onChange={(e) => setOpt(j, { text: e.target.value })} className="h-9 flex-1 text-sm" data-testid={`sjt-opt-${i}-${j}`} />
          <select value={o.score} onChange={(e) => setOpt(j, { score: Number(e.target.value) })} className="h-9 rounded-lg border border-line bg-white px-2 text-xs" title="Points for this answer" data-testid={`sjt-score-${i}-${j}`}>
            {[3, 2, 1, 0].map((v) => <option key={v} value={v} label={`${v} pts`} />)}
          </select>
        </div>
      ))}
    </div>
  );
}

const blank = () => ({ type: "team", scenario: "", options: [3, 2, 1, 0].map((score) => ({ text: "", score })) });

export function SjtEditor({ jobId, canEdit }) {
  const qc = useQueryClient();
  const key = ["sjt", jobId];
  const { data: s } = useQuery({ queryKey: key, queryFn: () => get(`/jobs/${jobId}/sjt`), refetchInterval: (q) => (q.state.data?.status === "generating" ? 3000 : false) });
  const [edit, setEdit] = useState(null);
  if (!s) return <Skel className="h-40" />;
  const run = async (fn, msg) => { try { const { data } = await fn(); qc.setQueryData(key, data); if (msg) toast.success(msg); return true; } catch (e) { toast.error(errMsg(e)); return false; } };
  const save = async () => { if (await run(() => api.put(`/jobs/${jobId}/sjt`, { scenarios: edit }), "Scenarios saved as draft")) setEdit(null); };
  const list = edit || s.scenarios;

  return (
    <section className="panel space-y-4 p-5 sm:p-6" data-testid="sjt-editor">
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="text-base font-semibold">Situational judgement scenarios</h3>
        <StatusChip status={CHIP[s.status]} testId="sjt-status" /><span className="text-xs text-ink-muted" data-testid="sjt-status-label">{LABEL[s.status]}</span>
        {canEdit && (
          <div className="ml-auto flex flex-wrap gap-2">
            {["none", "failed", "skipped", "draft", "approved"].includes(s.status) && !edit && <Button size="sm" variant="outline" onClick={() => run(() => api.post(`/jobs/${jobId}/sjt/generate`), "Drafting scenarios in the background")} data-testid="sjt-generate-btn"><PenLine size={14} /> {s.scenarios.length ? "Redraft with AI" : "Draft with AI"}</Button>}
            {s.scenarios.length > 0 && !edit && <Button size="sm" variant="outline" onClick={() => setEdit(s.scenarios)} data-testid="sjt-edit-btn">Edit</Button>}
            {edit && <><Button size="sm" variant="ghost" onClick={() => setEdit(null)} data-testid="sjt-cancel-btn">Cancel</Button><Button size="sm" onClick={save} data-testid="sjt-save-btn">Save draft</Button></>}
            {s.status === "draft" && !edit && <Button size="sm" onClick={() => run(() => api.post(`/jobs/${jobId}/sjt/approve`), "Scenarios approved")} data-testid="sjt-approve-btn">Approve</Button>}
            {s.status !== "skipped" && s.status !== "approved" && !edit && <Button size="sm" variant="ghost" onClick={() => run(() => api.post(`/jobs/${jobId}/sjt/skip`), "SJT skipped — invites can be sent")} data-testid="sjt-skip-btn">Skip SJT</Button>}
          </div>
        )}
      </div>
      <p className="text-sm text-ink-secondary">Candidates answer these after the questionnaire. Each scenario has four options worth 3, 2, 1 and 0 points. Until you approve them, invites still go out and candidates simply skip this part.</p>
      {s.status === "generating" && <p className="flex items-center gap-2 text-sm text-ink-secondary" data-testid="sjt-generating"><Loader2 size={14} className="animate-spin" /> Drafting 8–12 scenarios from the job description…</p>}
      {s.status === "failed" && <p className="text-sm text-risk">{s.error}</p>}
      {edit ? (
        <div className="space-y-3">
          {edit.map((sc, i) => <ScenarioEditor key={sc.id || i} s={sc} i={i} onChange={(v) => setEdit(edit.map((x, k) => (k === i ? v : x)))} onRemove={() => setEdit(edit.filter((_, k) => k !== i))} />)}
          {edit.length < 12 && <Button size="sm" variant="outline" onClick={() => setEdit([...edit, blank()])} data-testid="sjt-add-btn"><Plus size={14} /> Add scenario</Button>}
        </div>
      ) : list.length > 0 && (
        <ol className="space-y-2" data-testid="sjt-list">
          {list.map((sc, i) => (
            <li key={sc.id} className="rounded-[6px] border border-line p-3 text-sm">
              <p className="mb-1 text-xs capitalize text-ink-muted">{sc.type}</p><p className="text-ink">{sc.scenario}</p>
              <p className="mt-1 text-xs text-success">Best: {sc.options.find((o) => o.score === 3)?.text}</p>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
